/**
 * Labelled text input.
 *
 * The prototype repeated the same input style at a dozen sites and its label was
 * usually a bare <p>, so most fields had no programmatic label at all — only the
 * four that were given an explicit htmlFor did. This generates an id when one
 * isn't supplied and always ties the label to the input, without changing any
 * visual value: same surface, border, radius, font size, and the 32px left padding
 * used when there is a leading icon.
 */
import { useId, useState } from 'react';
import type { CSSProperties, ReactNode, Ref } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { c } from '../../styles/theme';

export interface TextFieldProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /**
   * Input type.
   *
   * `date` was added for the Publisher's release date, which is a real `<input
   * type="date">` rather than a text field with a format hint: the native control
   * gives a picker, keyboard entry, and a value already in the ISO form the API wants.
   * No style changes — the field renders with the same surface, border and padding as
   * every other one.
   */
  type?: 'text' | 'email' | 'password' | 'date';
  /** Leading icon, absolutely positioned inside the field. */
  icon?: ReactNode;
  /** Renders a textarea instead of an input. */
  multiline?: boolean;
  rows?: number;
  id?: string;
  /** Accessible name for fields drawn without a visible label. */
  ariaLabel?: string;
  /**
   * A ref onto the input itself, for a caller that has to focus it.
   *
   * Settings' name editor needs it: the field appears when an edit icon is pressed, and a
   * revealed input that does not take the caret makes one action cost two clicks. Only the
   * single-line input carries it — the caller wanting focus wants a text box, and typing
   * `HTMLInputElement | HTMLTextAreaElement` here would push that union onto every consumer
   * for the benefit of none.
   *
   * Deliberately not `forwardRef` on the component: this file is a labelled field, not the
   * input, and a ref to the wrapper is not what anyone asking for one wants.
   */
  inputRef?: Ref<HTMLInputElement>;
  /**
   * Whether the current value has been rejected.
   *
   * Sets `aria-invalid` and draws the border in the danger colour. Pair it with
   * `describedBy` pointing at the element that says WHY — on its own this states that
   * something is wrong without saying what, which is the half of the message a screen
   * reader user cannot get from the colour.
   *
   * Optional and unset by default, so no existing field changes appearance. The pattern is
   * TrimPanel's, which does the same two attributes by hand on a raw input; this is the same
   * thing on the shared field so the next caller does not have to stop using it to get them.
   */
  invalid?: boolean;
  /**
   * Id of the element describing this field — usually the validation message.
   *
   * Passed straight through to `aria-describedby`, so the reason is read as part of the
   * field rather than being a paragraph underneath that happens to be about it.
   */
  describedBy?: string;
  /** Monospace, for values like the DELETE confirmation. */
  mono?: boolean;
  /** Visible but not editable. */
  readOnly?: boolean;
  /** Visible but unavailable for interaction or submission. */
  disabled?: boolean;
  /** Participates in native form required-field validation. */
  required?: boolean;
  /** Bottom margin, which varies per site in the source. */
  marginBottom?: number;
  style?: CSSProperties;
}

export default function TextField({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  icon,
  multiline,
  rows = 2,
  id,
  ariaLabel,
  inputRef,
  invalid,
  describedBy,
  mono,
  readOnly,
  disabled,
  required,
  marginBottom,
  style,
}: TextFieldProps) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  const [revealed, setRevealed] = useState(false);
  const passwordField = !multiline && type === 'password';
  const effectiveType = passwordField && revealed ? 'text' : type;

  const fieldStyle: CSSProperties = {
    width: '100%',
    background: c.surface2,
    // The danger border only when a caller says so, so every field that does not pass
    // `invalid` renders exactly the same pixels it did before.
    border: `1px solid ${invalid ? c.danger : c.border}`,
    borderRadius: 8,
    fontSize: multiline ? 12 : 13,
    padding: icon
      ? passwordField
        ? '9px 38px 9px 32px'
        : '9px 10px 9px 32px'
      : multiline
        ? '8px 10px'
        : passwordField
          ? '9px 38px 9px 10px'
          : '9px 10px',
    color: c.text1,
    fontFamily: mono ? 'IBM Plex Mono' : 'Inter',
    boxSizing: 'border-box',
    ...(multiline ? { resize: 'none' as const } : null),
    ...style,
  };

  return (
    <div style={{ marginBottom }}>
      {label && (
        <label
          htmlFor={fieldId}
          style={{ fontSize: 12, color: c.text2, display: 'block', margin: '0 0 6px' }}
        >
          {label}
        </label>
      )}
      <div style={{ position: 'relative' }}>
        {icon && (
          <span
            style={{
              position: 'absolute',
              left: 10,
              top: '50%',
              transform: 'translateY(-50%)',
              display: 'flex',
              pointerEvents: 'none',
            }}
            aria-hidden="true"
          >
            {icon}
          </span>
        )}
        {multiline ? (
          <textarea
            id={fieldId}
            rows={rows}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            aria-label={label ? undefined : ariaLabel}
            // `|| undefined` rather than the boolean: aria-invalid="false" is a valid
            // announcement of validity, and no field here means to make it.
            aria-invalid={invalid || undefined}
            aria-describedby={describedBy}
            readOnly={readOnly}
            disabled={disabled}
            required={required}
            style={{
              ...fieldStyle,
              cursor: readOnly || disabled ? 'default' : fieldStyle.cursor,
              opacity: disabled ? 0.55 : readOnly ? 0.85 : fieldStyle.opacity,
            }}
          />
        ) : (
          <>
            <input
              ref={inputRef}
              id={fieldId}
              type={effectiveType}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder={placeholder}
              aria-label={label ? undefined : ariaLabel}
              aria-invalid={invalid || undefined}
              aria-describedby={describedBy}
              readOnly={readOnly}
              disabled={disabled}
              required={required}
              style={{
                ...fieldStyle,
                cursor: readOnly || disabled ? 'default' : fieldStyle.cursor,
                opacity: disabled ? 0.55 : readOnly ? 0.85 : fieldStyle.opacity,
              }}
            />
            {passwordField && (
              <button
                type="button"
                onClick={() => setRevealed((v) => !v)}
                aria-label={revealed ? 'Hide password' : 'Show password'}
                style={{
                  position: 'absolute',
                  right: 10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  display: 'flex',
                  cursor: 'pointer',
                  color: c.text2,
                }}
              >
                {revealed ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
